/* eslint-disable react/jsx-props-no-spreading */
import Input from 'components/common/input/Input'
import { ErrorMessage, useFormikContext } from 'formik'
import { useDropzone } from 'react-dropzone'
import React, { useEffect, useMemo, useState } from 'react'
import { EVENT_TYPE } from 'constants/Events'
import SelectComponent from 'components/common/select/SelectComponent'

// import React, { useCallback } from 'react'
import { minioSingleFileUpload } from 'containers/events/Api'
import { getUsers } from 'containers/users/Api'
import { toast } from 'react-toastify'
import CustomToast from 'components/common/custom-toast/CustomToast'
import { alertTypes } from 'constants/Common'
import ResourceUploadPanel from './ResourceUploadPanel'

const EVENT_AUDIENCE_PRIVATE = 'private'

function orgUserRowId(u) {
  if (!u) return undefined
  if (u.id !== undefined && u.id !== null && u.id !== '') return u.id
  // eslint-disable-next-line no-underscore-dangle
  return u._id
}

function FirstStep() {
  const formik = useFormikContext()
  const [fileLoading, setFileLoading] = useState(false)
  const [ContentFileLoading, setContentFileLoading] = useState(false)
  const [preWorkFileLoading, setPreWorkFileLoading] = useState(false)

  const [uploadProgress, setUploadProgress] = useState(0)
  const [contentUploadProgress, setContentUploadProgress] = useState(0)
  const [preUploadProgress, setPreUploadProgress] = useState(0)
  const [isCoverOpen, setIsCoverOpen] = useState(false)
  const [isContentOpen, setIsContentOpen] = useState(false)
  const [isPreWorkOpen, setIsPreWorkOpen] = useState(false)
  const [orgUserOptions, setOrgUserOptions] = useState([])
  const [orgUsersLoading, setOrgUsersLoading] = useState(true)

  const isPrivateEvent = formik.values.audience === EVENT_AUDIENCE_PRIVATE

  const selectedInviteeOptions = useMemo(() => {
    const ids = formik.values.inviteUserIds || []
    return orgUserOptions.filter((o) => ids.includes(o.value))
  }, [formik.values.inviteUserIds, orgUserOptions])

  useEffect(() => {
    let cancelled = false
    const loadOrgUsers = async () => {
      setOrgUsersLoading(true)
      const result = await getUsers(1, 500, '', '')
      if (cancelled) return
      const rows = Array.isArray(result?.data) ? result.data : []
      const opts = rows
        .map((u) => {
          const id = orgUserRowId(u)
          if (id === undefined || id === null || id === '') return null
          const label = u?.name || u?.email || u?.userName || String(id)
          return { value: String(id), label }
        })
        .filter(Boolean)
      setOrgUserOptions(opts)
      setOrgUsersLoading(false)
    }
    loadOrgUsers()
    return () => {
      cancelled = true
    }
  }, [])

  const handleFileUpload = async (file, contentType) => {
    const formData = new FormData()
    formData.append('file', file, 'file')
    formData.append('project', 'xler')
    formData.append('fileType', file.type)

    const onProgress = (percent) => {
      if (contentType === 'photo') {
        setFileLoading(true)
        setUploadProgress(percent)
      } else if (contentType === 'content') {
        setContentFileLoading(true)
        setContentUploadProgress(percent)
      } else if (contentType === 'preWork') {
        setPreWorkFileLoading(true)
        setPreUploadProgress(percent)
      }
    }

    const result = await minioSingleFileUpload(formData, onProgress)
    if (result?.message) {
      if (result?.paths)
        formik.setFieldValue(contentType, [
          ...(formik.values[contentType] || []),
          { link: result.paths, name: file.name },
        ])

      setFileLoading(false)
      setContentFileLoading(false)
      setPreWorkFileLoading(false)
      setUploadProgress(0)
      setContentUploadProgress(0)
      setPreUploadProgress(0)
      toast(
        <CustomToast
          variant={alertTypes.SUCCESS}
          message={result?.message || 'Successfully!'}
        />
      )
    } else {
      setFileLoading(false)
      setContentFileLoading(false)
      setPreWorkFileLoading(false)
      setUploadProgress(0)
      setContentUploadProgress(0)
      setPreUploadProgress(0)
      toast(
        <CustomToast
          variant={alertTypes.DANGER}
          message={result?.response?.data?.error}
        />
      )
    }
  }

  const createOnDrop = (contentType) => (files) => {
    if (files.length > 0) {
      const uploadedFiles = Array.from(files)
      uploadedFiles.forEach((file) => handleFileUpload(file, contentType))
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: createOnDrop('photo'),
    accept: 'image/svg+xml, image/png, image/jpeg, image/gif, video/*',
    // maxSize: 800 * 400,
    maxSize: 50 * 1024 * 1024,
    onDropRejected: (fileRejections) => {
      fileRejections.forEach((rejection) => {
        rejection.errors.forEach((error) => {
          if (error.code === 'file-too-large') {
            toast(
              <CustomToast
                variant={alertTypes.DANGER}
                message={`File too large: ${rejection.file.name} please upload file size less than 50mb`}
              />
            )
          }
        })
      })
    },
  })

  const { getRootProps: getRootProps2, getInputProps: getInputProps2 } =
    useDropzone({
      onDrop: createOnDrop('content'),
      accept: {
        'application/pdf': [], // PDF files
        'application/vnd.ms-powerpoint': [], // PPT files
        'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          [], // PPTX files
      },
      maxSize: 50 * 1024 * 1024,
      onDropRejected: (fileRejections) => {
        fileRejections.forEach((rejection) => {
          rejection.errors.forEach((error) => {
            if (error.code === 'file-too-large') {
              toast(
                <CustomToast
                  variant={alertTypes.DANGER}
                  message={`File too large: ${rejection.file.name} please upload file size less than 50mb`}
                />
              )
            } else if (error.code === 'file-invalid-type') {
              toast(
                <CustomToast
                  variant={alertTypes.DANGER}
                  message={`Invalid file type: ${rejection.file.name} only PDF,PPT,PPTX formats allowed`}
                />
              )
            }
          })
        })
      },
    })

  const { getRootProps: getRootProps3, getInputProps: getInputProps3 } =
    useDropzone({
      onDrop: createOnDrop('preWork'),
      accept: {
        'application/pdf': [], // PDF files
        'application/vnd.ms-powerpoint': [], // PPT files
        'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          [], // PPTX files
      },
      maxSize: 50 * 1024 * 1024,
      onDropRejected: (fileRejections) => {
        fileRejections.forEach((rejection) => {
          rejection.errors.forEach((error) => {
            if (error.code === 'file-too-large') {
              toast(
                <CustomToast
                  variant={alertTypes.DANGER}
                  message={`File too large: ${rejection.file.name} please upload file size less than 50mb`}
                />
              )
            } else if (error.code === 'file-invalid-type') {
              toast(
                <CustomToast
                  variant={alertTypes.DANGER}
                  message={`Invalid file type: ${rejection.file.name} only PDF,PPT,PPTX formats allowed`}
                />
              )
            }
          })
        })
      },
    })
  const removeFile = (file, type) => {
    formik.setFieldValue(
      type,
      formik.values[type].filter((item) => item.name !== file.name)
    )
  }

  const handleAttachResourceLink = (inputKey, listKey) => {
    const rawValue = formik.values[inputKey]
    const value = String(rawValue || '').trim()
    if (!value) return

    formik.setFieldValue(listKey, [
      ...(formik.values[listKey] || []),
      {
        link: value,
        name: value,
      },
    ])
    formik.setFieldValue(inputKey, '')
  }

  let inviteSelectPlaceholder = 'Select User'
  if (orgUsersLoading) {
    inviteSelectPlaceholder = 'Loading users…'
  } else if (!isPrivateEvent) {
    inviteSelectPlaceholder = 'Available for private events only'
  }

  return (
    <div className='event-create-card'>
      <p className='heading-lg mb-2'>About</p>
      <div className='event-field'>
        <p className='event-field__label'>Event Type</p>
        <div className='event-field__control event-field__control--select'>
          <SelectComponent
            name='audience'
            options={EVENT_TYPE}
            selectedValue={formik.values.audience}
            placeholder='Select Event Type'
            handleChange={(obj) => {
              const nextAudience = obj?.value || ''
              formik.setFieldValue('audience', nextAudience)
              if (nextAudience !== EVENT_AUDIENCE_PRIVATE) {
                formik.setFieldValue('inviteUserIds', [])
              }
            }}
          />
        </div>
      </div>
      <ErrorMessage className='error-text' component='p' name='audience' />

      <div className='event-field'>
        <p className='event-field__label'>Select User</p>
        <div className='event-field__control event-field__control--select'>
          <SelectComponent
            isMulti
            options={orgUserOptions}
            selectedValue={selectedInviteeOptions}
            placeholder={inviteSelectPlaceholder}
            isDisabled={!isPrivateEvent || orgUsersLoading}
            isLoading={orgUsersLoading}
            handleChange={(selected) => {
              const list = selected || []
              formik.setFieldValue(
                'inviteUserIds',
                list.map((item) => item.value)
              )
            }}
          />
        </div>
        {isPrivateEvent && selectedInviteeOptions?.length ? (
          <div className='event-tag-row'>
            {selectedInviteeOptions.map((user) => (
              <div key={user.value} className='event-tag'>
                <span>{user.label}</span>
                <button
                  type='button'
                  className='event-tag__remove'
                  aria-label={`Remove ${user.label}`}
                  onClick={() =>
                    formik.setFieldValue(
                      'inviteUserIds',
                      (formik.values.inviteUserIds || []).filter(
                        (id) => id !== user.value
                      )
                    )
                  }
                >
                  x
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className='event-field'>
        <p className='event-field__label'>Event Name</p>
        <div className='event-field__control'>
          <Input
            value={formik.values.name}
            name='name'
            handleChange={formik.handleChange}
            placeholder='Add Event name'
            label=''
          />
        </div>
        <ErrorMessage className='error-text' component='p' name='name' />
      </div>

      <div className='event-field'>
        <p className='event-field__label'>Event Sub Heading</p>
        <div className='event-field__control'>
          <Input
            name='subHeading'
            value={formik.values.subHeading}
            handleChange={formik.handleChange}
            placeholder='Event Sub Heading'
            label=''
          />
        </div>
        <ErrorMessage className='error-text' component='p' name='subHeading' />
      </div>
      <div className='event-field'>
        <p className='event-field__label'>Event Description</p>
        <div className='event-field__control'>
          <Input
            name='description'
            type='textarea'
            value={formik.values.description}
            handleChange={formik.handleChange}
            placeholder='Event Description'
            label=''
          />
        </div>
        <ErrorMessage className='error-text' component='p' name='description' />
      </div>

      <ResourceUploadPanel
        panelId='event-cover-panel'
        title='Cover media'
        isOpen={isCoverOpen}
        onToggle={() => setIsCoverOpen((prev) => !prev)}
        inputName='photoInput'
        inputValue={formik.values.photoInput}
        onInputChange={formik.handleChange}
        onAttachLink={() => handleAttachResourceLink('photoInput', 'photo')}
        items={formik.values.photo || []}
        onRemoveItem={(file) => removeFile(file, 'photo')}
        loading={fileLoading}
        uploadProgress={uploadProgress}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        uploadHint='SVG, PNG, JPG, VIDEO or GIF (max. 50mb)'
      />

      <ResourceUploadPanel
        panelId='event-content-panel'
        title='Content'
        isOpen={isContentOpen}
        onToggle={() => setIsContentOpen((prev) => !prev)}
        inputName='ContentInput'
        inputValue={formik.values.ContentInput}
        onInputChange={formik.handleChange}
        onAttachLink={() => handleAttachResourceLink('ContentInput', 'content')}
        items={formik.values.content || []}
        onRemoveItem={(file) => removeFile(file, 'content')}
        loading={ContentFileLoading}
        uploadProgress={contentUploadProgress}
        getRootProps={getRootProps2}
        getInputProps={getInputProps2}
      />

      <ResourceUploadPanel
        panelId='event-prework-panel'
        title='Pre work'
        isOpen={isPreWorkOpen}
        onToggle={() => setIsPreWorkOpen((prev) => !prev)}
        inputName='preWorkInput'
        inputValue={formik.values.preWorkInput}
        onInputChange={formik.handleChange}
        onAttachLink={() => handleAttachResourceLink('preWorkInput', 'preWork')}
        items={formik.values.preWork || []}
        onRemoveItem={(file) => removeFile(file, 'preWork')}
        loading={preWorkFileLoading}
        uploadProgress={preUploadProgress}
        getRootProps={getRootProps3}
        getInputProps={getInputProps3}
      />
    </div>
  )
}

export default FirstStep
